"use client";

import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import { evaluateChecklist, type ChecklistSchema } from "@/lib/gamification/checklist-engine";
import {
  captureGamificationResultForCrm,
  gamificationTrackingAttributes,
  trackGamificationEngagement,
  type GamificationTrackingContext,
} from "@/components/gamification/tracking";
import PlayableExperienceCanvas from "@/components/gamification/PlayableExperienceCanvas";
import { useWebMcpTool } from "@/lib/webmcp/react";

export type InteractiveChecklistToolCopy = {
  readonly eyebrow: string;
  readonly title: string;
  readonly intro: string;
  readonly progressLabel: string;
  readonly completedLabel: string;
  readonly resultLabel: string;
  readonly remainingLabel: string;
  readonly resetLabel: string;
};

type InteractiveChecklistToolProps = {
  readonly copy: InteractiveChecklistToolCopy;
  readonly schema: ChecklistSchema;
  readonly tracking: GamificationTrackingContext;
};

export default function InteractiveChecklistTool({ copy, schema, tracking }: InteractiveChecklistToolProps) {
  const [completedIds, setCompletedIds] = useState<readonly string[]>([]);
  const startedRef = useRef(false);
  const capturedTierRef = useRef("");
  const result = useMemo(() => evaluateChecklist(schema, completedIds), [schema, completedIds]);
  const firstIncompleteId = result.incompleteItems[0]?.id;
  const canvasNodes = schema.items.map((item) => ({
    id: item.id,
    label: item.label,
    detail: item.description,
    active: item.id === firstIncompleteId,
    complete: completedIds.includes(item.id),
  }));
  const canvasTracks = result.incompleteItems.slice(0, 4).map((item) => ({
    id: item.id,
    label: item.label,
    detail: item.description,
    active: item.id === firstIncompleteId,
  }));
  const toolSchema = useMemo(
    () => z.object({ completedIds: z.array(z.enum(schema.items.map((item) => item.id) as [string, ...string[]])) }).strict(),
    [schema.items],
  );
  useWebMcpTool({
    action: "update_checklist",
    instance: schema.id.replaceAll("-", "_"),
    schema: toolSchema,
    execute: (input) => {
      const nextResult = evaluateChecklist(schema, input.completedIds);
      setCompletedIds(input.completedIds);
      return { status: "success", uiState: nextResult };
    },
  });

  function trackStart() {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    trackGamificationEngagement(tracking, `${tracking.componentId}.start`, { xp_zone: "progress" });
  }

  function toggleItem(id: string) {
    trackStart();
    setCompletedIds((current) => {
      const next = current.includes(id) ? current.filter((itemId) => itemId !== id) : [...current, id];
      const nextResult = evaluateChecklist(schema, next);

      if (nextResult.complete || nextResult.percent >= 50) {
        if (capturedTierRef.current !== nextResult.tier.id) {
          capturedTierRef.current = nextResult.tier.id;
          captureGamificationResultForCrm(tracking, nextResult.tier.id);
        }

        trackGamificationEngagement(tracking, `${tracking.componentId}.progress`, {
          result_tier: nextResult.tier.id,
          variant_key: nextResult.tier.id,
          xp_zone: "progress",
        });
      }

      return next;
    });
  }

  return (
    <section
      id={schema.id}
      className="redesign-section redesign-section--muted gamification-tool"
      aria-labelledby={`${schema.id}-title`}
      data-gamification-tool={schema.id}
      {...gamificationTrackingAttributes(tracking)}
    >
      <div className="container">
        <div className="marketing-section-heading marketing-section-heading--left">
          <p className="marketing-eyebrow">{copy.eyebrow}</p>
          <h2 id={`${schema.id}-title`}>{copy.title}</h2>
          <p>{copy.intro}</p>
        </div>

        <div className="gamification-tool__grid">
          <div className="gamification-tool__panel redesign-list-card">
            <div
              className="gamification-tool__progress"
              role="progressbar"
              aria-label={copy.progressLabel}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={result.percent}
            >
              <span style={{ width: `${result.percent}%` }} />
            </div>
            <p className="gamification-tool__progress-label">
              {result.completedCount}/{result.totalCount} {copy.completedLabel}
            </p>
            <div className="gamification-tool__options">
              {schema.items.map((item) => (
                <label className="gamification-tool__option" key={item.id}>
                  <input
                    type="checkbox"
                    checked={completedIds.includes(item.id)}
                    onChange={() => toggleItem(item.id)}
                  />
                  <span>
                    <strong>{item.label}</strong>
                    {item.description ? <small>{item.description}</small> : null}
                  </span>
                </label>
              ))}
            </div>
            <button
              type="button"
              className="gamification-tool__link-button"
              onClick={() => {
                setCompletedIds([]);
                startedRef.current = false;
                capturedTierRef.current = "";
              }}
            >
              {copy.resetLabel}
            </button>
          </div>

          <PlayableExperienceCanvas
            ariaLabel={copy.title}
            id={schema.id}
            nodes={canvasNodes}
            primaryLabel={result.tier.label}
            progress={result.percent}
            secondaryLabel={result.tier.summary}
            tracks={canvasTracks}
            variant="checklist"
          />

          <aside className="gamification-tool__result redesign-list-card" aria-live="polite">
            <p className="marketing-eyebrow">{copy.resultLabel}</p>
            <h3>{result.tier.label}</h3>
            <p>{result.tier.summary}</p>
            {result.incompleteItems.length > 0 ? (
              <div className="gamification-tool__result-block">
                <h4>{copy.remainingLabel}</h4>
                <ul>
                  {result.incompleteItems.slice(0, 4).map((item) => (
                    <li key={item.id}>{item.label}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </section>
  );
}
